import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { IconNavCollapse } from '../ui/icons';

function RailLink({ to, end, label, Icon, expanded }) {
  return (
    <li className={expanded ? undefined : 'flex justify-center'}>
      <NavLink
        to={to}
        end={end}
        title={expanded ? undefined : label}
        className={({ isActive }) => {
          const baseRow =
            'flex items-center gap-2.5 rounded-xl text-sm font-medium text-white outline-none transition-colors focus-visible:ring-2 focus-visible:ring-white/30';
          const state = isActive ? 'bg-[#16335D]' : 'hover:bg-[#16335D]/70';
          const layout = expanded
            ? 'h-9 w-full px-2.5'
            : 'h-10 w-10 justify-center';
          return `${baseRow} ${state} ${layout}`;
        }}
      >
        <Icon className="h-5 w-5 shrink-0 text-white" />
        {expanded ? <span className="min-w-0 flex-1 truncate">{label}</span> : null}
      </NavLink>
    </li>
  );
}

const HOVER_OPEN_MS = 450;
const HOVER_CLOSE_MS = 200;

export function WorkspaceNav({ ariaLabel, sections }) {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [forceClosed, setForceClosed] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const expanded = pinned || (hovered && !forceClosed);

  useEffect(
    () => () => {
      window.clearTimeout(openTimer.current);
      window.clearTimeout(closeTimer.current);
    },
    []
  );

  function reveal() {
    window.clearTimeout(closeTimer.current);
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(() => {
      setHovered(true);
      setForceClosed(false);
    }, HOVER_OPEN_MS);
  }

  function conceal() {
    window.clearTimeout(openTimer.current);
    closeTimer.current = window.setTimeout(() => setHovered(false), HOVER_CLOSE_MS);
  }

  function togglePinned() {
    if (expanded && !pinned) {
      setForceClosed(true);
      return;
    }
    setForceClosed(false);
    setPinned((value) => !value);
  }

  return (
    <aside
      className={`mb-3 ml-3 mr-1 flex min-h-0 shrink-0 flex-col overflow-hidden rounded-2xl border border-[#313843] bg-[#030E29] transition-[width] duration-300 ease-out ${
        expanded ? 'w-[232px]' : 'w-[68px]'
      }`}
      aria-label={ariaLabel}
      onMouseEnter={reveal}
      onMouseLeave={conceal}
    >
      <nav
        className={`workspace-nav-scroll flex-1 overflow-x-hidden overflow-y-auto pt-4 ${
          expanded ? 'px-3' : 'px-2'
        }`}
      >
        <div className="flex flex-col">
          {sections.map((section, index) => (
            <div
              key={section.label || section.items[0]?.to || index}
              className={index === 0 ? '' : expanded ? 'mt-1' : 'mt-2'}
            >
              {expanded && section.label ? (
                <p className="px-2.5 py-1.5 text-[10px] font-semibold uppercase leading-5 tracking-[0.12em] text-white/70">
                  {section.label}
                </p>
              ) : null}
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {section.items.map((item) => (
                  <RailLink key={item.to} {...item} expanded={expanded} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      <div className={`pb-3 pt-2 ${expanded ? 'px-3' : 'flex justify-center px-2'}`}>
        <button
          type="button"
          onClick={togglePinned}
          aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
          aria-pressed={expanded}
          className={`flex items-center gap-2.5 rounded-xl text-[#D1D5DB] transition-colors hover:bg-[#16335D]/70 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
            expanded ? 'h-9 w-full px-2.5' : 'h-10 w-10 justify-center'
          }`}
        >
          <IconNavCollapse className="h-5 w-5 shrink-0" />
          {expanded ? (
            <span className="text-sm font-medium tracking-[-0.014em]">
              Collapse menu
            </span>
          ) : null}
        </button>
      </div>
    </aside>
  );
}
